// frontend/src/pages/UserGuidePage.tsx
import { Link } from 'react-router-dom';
import { InfoPageLayout } from './InfoPageLayout';
import { 
  MessageSquare, 
  Sparkles, 
  Globe, 
  Settings, 
  Key, 
  Wand2, 
  Play,
  Upload,
  DollarSign,
  Eye,
  Code
} from 'lucide-react';

export function UserGuidePage() {
  return (
    <InfoPageLayout title="User Guide">
      <p className="text-lg">
        Welcome to Llambada! This guide will help you get the most out of the platform, 
        whether you're building your first mini-app or looking to publish and earn money 
        as a creator.
      </p>

      <h2>Getting Started</h2>
      
      <h3><MessageSquare size={20} className="inline mr-2" />Creating Your First Mini-App</h3>
      <p>
        Building a mini-app on Llambada is as simple as having a conversation. Here's how:
      </p>
      <ol>
        <li>
          <strong>Go to your Dashboard</strong> and click "New Mini-App" or use the search bar 
          on the home page to describe what you want to build
        </li>
        <li>
          <strong>Describe your idea</strong> in plain language. For example: "I want a tool 
          that converts PDF documents to text files" or "Build me a calculator that computes 
          mortgage payments"
        </li>
        <li>
          <strong>Watch the AI work</strong>. The AI will create both the user interface 
          (frontend) and the logic (backend) for your mini-app
        </li>
        <li>
          <strong>Test it</strong>. Once built, you can immediately try your mini-app to see 
          if it works as expected
        </li>
        <li>
          <strong>Refine through conversation</strong>. If something isn't right, just tell 
          the AI what to change: "Make the button blue," "Add an option to download the result," 
          or "Handle the case when the file is too large"
        </li>
      </ol>

      <h3><Sparkles size={20} className="inline mr-2" />Tips for Better Results</h3>
      <ul>
        <li>
          <strong>Be specific about inputs and outputs:</strong> Instead of "build a converter," 
          say "build a tool that takes a CSV file and converts it to JSON format"
        </li>
        <li>
          <strong>Describe the user experience:</strong> "Show a file upload button, then display 
          a progress bar while processing, then show a download link"
        </li>
        <li>
          <strong>Mention edge cases:</strong> "If the file is larger than 10MB, show an error 
          message" or "If the input is empty, disable the submit button"
        </li>
        <li>
          <strong>Iterate in small steps:</strong> Get the basic functionality working first, 
          then add features one at a time
        </li>
      </ul>

      <h2>Using Mini-Apps</h2>

      <h3><Play size={20} className="inline mr-2" />Running Mini-Apps</h3>
      <p>
        Mini-apps run directly in your browser. Most mini-apps have a simple interface where you:
      </p>
      <ol>
        <li>Enter data or upload files</li>
        <li>Click a button to process</li>
        <li>See results or download output files</li>
      </ol>

      <h3><Upload size={20} className="inline mr-2" />File Uploads</h3>
      <p>
        Many mini-apps accept file uploads. When uploading files:
      </p>
      <ul>
        <li>Check the accepted file types (usually shown near the upload button)</li>
        <li>Be aware of size limits (typically a few MB)</li>
        <li>Your files are processed securely and not stored longer than necessary</li>
      </ul>

      <h3><Wand2 size={20} className="inline mr-2" />Customizing Mini-Apps</h3>
      <p>
        Found a mini-app that's almost what you need? Click the <strong>Customize</strong> button 
        to create your own version. This creates a copy that you can modify through conversation 
        with the AI, just like building from scratch.
      </p>
      <p>
        Customizing is a great way to:
      </p>
      <ul>
        <li>Adapt an existing mini-app to your specific needs</li>
        <li>Learn how mini-apps work by examining and modifying them</li>
        <li>Build on the work of other creators</li>
      </ul>

      <h2>Your Dashboard</h2>

      <h3><Eye size={20} className="inline mr-2" />Managing Your Mini-Apps</h3>
      <p>
        Your dashboard shows all the mini-apps you've created. From here you can:
      </p>
      <ul>
        <li><strong>Open</strong> a mini-app to continue working on it</li>
        <li><strong>Test</strong> your mini-app before publishing</li>
        <li><strong>Publish</strong> to make it available to others (free plan includes one public mini-app)</li>
        <li><strong>Unpublish</strong> to make it private again</li>
        <li><strong>Delete</strong> mini-apps you no longer need</li>
      </ul>

      <h3><Code size={20} className="inline mr-2" />Viewing Code</h3>
      <p>
        While you don't need to understand code, you can view the generated code for your mini-apps. 
        This is useful for:
      </p>
      <ul>
        <li>Learning how your mini-app works</li>
        <li>Debugging issues</li>
        <li>Making manual adjustments (for advanced users)</li>
      </ul>

      <h2>Publishing Mini-Apps</h2>

      <h3><Globe size={20} className="inline mr-2" />Making Your Mini-App Public</h3>
      <p>
        Publishing makes your mini-app available to everyone. Before publishing:
      </p>
      <ul>
        <li>Test thoroughly to ensure it works correctly</li>
        <li>Write a clear name and description</li>
        <li>Consider potential misuse and add appropriate safeguards</li>
      </ul>
      <p>
        <strong>Note:</strong> Free users can publish one mini-app publicly (non-monetized). Paid subscribers can publish multiple mini-apps and earn revenue.
      </p>

      <h3><DollarSign size={20} className="inline mr-2" />Earning Money</h3>
      <p>
        When paid subscribers use your published mini-apps, you earn 70% of the attributed revenue. 
        To participate in revenue sharing:
      </p>
      <ol>
        <li>Have a paid subscription</li>
        <li>Publish at least one mini-app</li>
        <li>Set up your payout method via Stripe Connect (in your Earnings page)</li>
      </ol>
      <p>
        <Link to="/earnings">View your earnings →</Link>
      </p>

      <h2>Settings and Preferences</h2>

      <h3><Settings size={20} className="inline mr-2" />Account Settings</h3>
      <p>
        Access your settings from the gear icon in the dashboard. Here you can:
      </p>
      <ul>
        <li>Update your display name</li>
        <li>Change your email address</li>
        <li>Manage your subscription</li>
        <li>Set up payout information</li>
      </ul>

      <h3><Key size={20} className="inline mr-2" />Using Your Own API Key</h3>
      <p>
        Power users can bring their own Anthropic API key for unlimited AI usage:
      </p>
      <ol>
        <li>Get an API key from <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer">Anthropic's console</a></li>
        <li>Go to Settings in your Dashboard</li>
        <li>Enter your API key in the designated field</li>
        <li>Save your settings</li>
      </ol>
      <p>
        When you use your own API key, you pay Anthropic directly for AI usage. This is ideal 
        for heavy users or those who want full control over their AI costs.
      </p>

      <h2>Troubleshooting</h2>

      <h3>My mini-app isn't working correctly</h3>
      <ul>
        <li>Try describing the problem to the AI: "The download button doesn't work"</li>
        <li>Check if there are error messages displayed</li>
        <li>Try refreshing the page</li>
        <li>If the issue persists, contact support with details about the problem</li>
      </ul>

      <h3>The AI isn't understanding what I want</h3>
      <ul>
        <li>Try being more specific about inputs, outputs, and the process</li>
        <li>Break down complex requests into smaller steps</li>
        <li>Provide examples of what you expect</li>
        <li>Use simple, clear language</li>
      </ul>

      <h3>I've run out of AI credits</h3>
      <ul>
        <li>Wait for your credits to refresh (they reset periodically)</li>
        <li>Upgrade to a paid plan for more credits</li>
        <li>Add your own Anthropic API key for unlimited usage</li>
      </ul>

      <h3>I can't publish my mini-app</h3>
      <ul>
        <li>Free plan allows one published mini-app — upgrade to publish more</li>
        <li>Check that your mini-app has a name and description</li>
        <li>Make sure the mini-app has been built successfully</li>
      </ul>

      <h2>Best Practices</h2>

      <h3>For Building</h3>
      <ul>
        <li>Start simple and add features incrementally</li>
        <li>Test each change before moving on</li>
        <li>Save frequently (your work is auto-saved, but good habits help)</li>
        <li>Write clear descriptions for future reference</li>
      </ul>

      <h3>For Publishing</h3>
      <ul>
        <li>Choose descriptive names that explain what the mini-app does</li>
        <li>Write helpful descriptions that set correct expectations</li>
        <li>Test with various inputs, including edge cases</li>
        <li>Consider accessibility and usability</li>
      </ul>

      <h3>For Earning</h3>
      <ul>
        <li>Build mini-apps that solve real problems</li>
        <li>Focus on quality over quantity</li>
        <li>Respond to user feedback and improve your mini-apps</li>
        <li>Promote your mini-apps on social media and relevant communities</li>
      </ul>

      <h2>Need More Help?</h2>
      <p>
        Can't find what you're looking for? <Link to="/contact">Contact our support team</Link>—we're 
        here to help!
      </p>
      <p>
        Want to learn more about earning as a creator? Visit our <Link to="/creators">Creators page</Link>.
      </p>
    </InfoPageLayout>
  );
}
